import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Users, Loader2, Send, RotateCcw, Search } from 'lucide-react';
import { API_BASE_URL } from '../../config/apiConfig';
import { saveShareCreationData } from '../../api/AccountsAPI/shareCapitalAPI';
import { uploadFileToS3 } from '../../api/commonAPI/awsUploadAPI';
import { S3_FOLDERS } from '../../config/s3Config';

// Legacy /Accounts/ShareCreationDetails (Views/Accounts/ShareCreationDetails.cshtml) — ESOP share grant/exercise.
// Only "ESOP Scheme" opens the form (legacy hides it for "Public Issuance"). Employee picked by search
// ("Name (EmpRefNo)") → GetEmpDetails. Every field + a PDF is required. spSaveShareCreationData returns
// 'Submited$<file>'; the PDF is uploaded to ShareCreation under that name.

const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';
const readonlyCls = inputCls + ' bg-gray-50 dark:bg-gray-900/40';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);
const Card = ({ title, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        {title && <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-4">{title}</h2>}
        {children}
    </div>
);

const SCHEME_TYPES = [{ id: 'ESOP', label: 'ESOP Scheme' }, { id: 'Public', label: 'Public Issuance' }];
const STATUS_TYPES = [{ id: 'Issued', label: 'Issued' }, { id: 'NotIssued', label: 'Not Issued' }];

const empty = {
    EmployeeName: '', EmployeeId: '', Department: '', Position: '', EmpStartDate: '',
    VestingStartDate: '', VestingEndDate: '', VestingPercentage: '', ExercisePricePerShare: '',
    GrantID: '', GrantDate: '', NumberofSharesGranted: '', GrantStatus: '',
    OptionID: '', ExerciseDate: '', NumberofSharesExercised: '', ExercisePrice: '', ExerciseStatus: '',
    Date: '', Remarks: '',
};

const ShareCreationDetails = () => {
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const createdBy = userData?.userName || '';

    const [scheme, setScheme] = useState('');
    const [search, setSearch] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [f, setF] = useState(empty);
    const [file, setFile] = useState(null);
    const [fileKey, setFileKey] = useState(0);
    const [busy, setBusy] = useState(false);
    const timer = useRef(null);

    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    const setNum = (k, v, decimals) => {
        const re = decimals ? /^\d*\.?\d{0,2}$/ : /^\d*$/;
        if (v === '' || re.test(v)) set(k, v);
    };
    const exerciseAmount = f.NumberofSharesExercised !== '' && f.ExercisePrice !== ''
        ? (parseFloat(f.NumberofSharesExercised) * parseFloat(f.ExercisePrice)).toFixed(2) : '';

    // legacy autocomplete: GetEmpDataForShareRequest(prefix) → [{ FirstName: "Name (EmpRefNo)" }]
    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        if (!search.trim() || search.includes('(')) { setSuggestions([]); return undefined; }
        timer.current = setTimeout(async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/Accounts/GetEmpDataForShareRequest`, { params: { Prefix: search.trim() } });
                const d = res?.data?.Data ?? res?.data ?? [];
                setSuggestions(Array.isArray(d) ? d.map((x) => x.FirstName).filter(Boolean) : []);
            } catch { setSuggestions([]); }
        }, 300);
        return () => clearTimeout(timer.current);
    }, [search]);

    const viewEmployee = async () => {
        const m = search.match(/\(([^)]+)\)/);
        if (!m) { toast.warn('Select an employee from the list'); return; }
        try {
            const res = await axios.get(`${API_BASE_URL}/Accounts/GetEmpDetails`, { params: { EmpRefNo: m[1] } });
            const d = (res?.data?.Data ?? res?.data ?? [])[0];
            if (!d) { toast.error('Employee not found'); return; }
            setF((p) => ({
                ...p,
                EmployeeName: d.EmployeeName || '', EmployeeId: d.EmployeeId || '',
                Department: d.Department || '', Position: d.Position || '', EmpStartDate: d.EmpStartDate || '',
            }));
        } catch { toast.error('Failed to load employee'); }
    };

    const pickPdf = (e) => {
        const x = e.target.files?.[0] || null;
        if (x && x.name.split('.').pop().toLowerCase() !== 'pdf') {
            toast.error('Invalid Upload File. Upload only PDF File'); e.target.value = ''; setFile(null); return;
        }
        setFile(x);
    };

    const reset = () => { setF(empty); setSearch(''); setSuggestions([]); setFile(null); setFileKey((k) => k + 1); };

    const handleSubmit = async () => {
        const checks = [
            ['EmployeeId', 'Invalid Employee'], ['VestingStartDate', 'Invalid Vesting Start Date'],
            ['VestingEndDate', 'Invalid Vesting End Date'], ['VestingPercentage', 'Invalid Vesting Percentage'],
            ['ExercisePricePerShare', 'Invalid Price Per Share'], ['GrantID', 'Invalid Grant ID'], ['GrantDate', 'Invalid Grant Date'],
            ['NumberofSharesGranted', 'Invalid Number of Shares Granted'], ['GrantStatus', 'Invalid Grant Status'],
            ['OptionID', 'Invalid Option ID'], ['ExerciseDate', 'Invalid Exercise Date'],
            ['NumberofSharesExercised', 'Invalid Number of Shares Exercised'], ['ExercisePrice', 'Invalid Exercise Price'],
        ];
        for (const [k, msg] of checks) { if (!String(f[k] || '').trim()) { toast.warn(msg); return; } }
        if (!exerciseAmount || Number(exerciseAmount) === 0) { toast.warn('Invalid Exercise Total'); return; }
        if (!f.ExerciseStatus) { toast.warn('Invalid Exercise Status'); return; }
        if (!file) { toast.warn('Upload Pdf File'); return; }
        if (!f.Date) { toast.warn('Select Date'); return; }
        if (!f.Remarks.trim()) { toast.warn('Enter Remarks'); return; }

        const payload = {
            EmployeeId: f.EmployeeId, VestingStartDate: f.VestingStartDate, VestingEndDate: f.VestingEndDate,
            VestingPercentage: f.VestingPercentage, ExercisePricePerShare: f.ExercisePricePerShare,
            GrantID: f.GrantID, GrantDate: f.GrantDate, NumberofSharesGranted: f.NumberofSharesGranted, GrantStatus: f.GrantStatus,
            OptionID: f.OptionID, ExerciseDate: f.ExerciseDate, NumberofSharesExercised: f.NumberofSharesExercised,
            ExercisePrice: f.ExercisePrice, ExerciseAmount: exerciseAmount, ExerciseStatus: f.ExerciseStatus,
            Date: f.Date, Remarks: f.Remarks.trim(),
            Filechk: 'Yes', Extension: file.name.split('.').pop(),
            RoleId: parseInt(roleId, 10) || 0, CreatedBy: createdBy,
        };
        setBusy(true);
        try {
            const res = await saveShareCreationData(payload);
            const status = String(res?.data?.Data ?? res?.data ?? '').trim();
            const [result, fileName] = status.split('$');
            if (result !== 'Submited') { toast.error(status || 'Failed to submit share creation'); return; }
            if (fileName) {
                try { await uploadFileToS3(file, S3_FOLDERS.SHARE_CREATION, fileName); }
                catch (e) { toast.warning(`Saved, but the PDF upload failed: ${e?.Message || e?.message || e}`); }
            }
            toast.success('Share Creation Details Submitted Successfully');
            reset();
        } catch (e) {
            toast.error(e?.response?.data?.Message || e?.message || 'Failed to submit share creation');
        } finally { setBusy(false); }
    };

    const field = (k, label, opts = {}) => (
        <div>
            <Label required={!opts.readOnly}>{label}</Label>
            {opts.select ? (
                <select className={inputCls} value={f[k]} onChange={(e) => set(k, e.target.value)} disabled={busy}>
                    <option value="">-Please Select-</option>
                    {opts.select.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
            ) : (
                <input type={opts.date ? 'date' : 'text'} className={opts.readOnly ? readonlyCls : inputCls}
                    readOnly={opts.readOnly} value={f[k]} disabled={busy && !opts.readOnly}
                    onChange={(e) => (opts.num !== undefined ? setNum(k, e.target.value, opts.num) : set(k, e.target.value))} />
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">
            <div className="max-w-5xl mx-auto space-y-5">
                <div className="rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 text-white flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center border border-white/20"><Users className="h-6 w-6" /></div>
                    <div>
                        <h1 className="text-xl font-black">Share Creation Details</h1>
                        <p className="text-sm text-white/70">ESOP grant and exercise</p>
                    </div>
                </div>

                <Card>
                    <div className="max-w-xs">
                        <Label required>Scheme Type</Label>
                        <select className={inputCls} value={scheme} onChange={(e) => { setScheme(e.target.value); reset(); }}>
                            <option value="">-Please Select-</option>
                            {SCHEME_TYPES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                    </div>
                </Card>

                {scheme === 'ESOP' && (
                    <>
                        <Card title="Employee">
                            <div className="flex gap-2 items-end mb-4">
                                <div className="flex-1 relative">
                                    <Label required>Employee Name</Label>
                                    <input className={inputCls + ' uppercase'} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee" />
                                    {suggestions.length > 0 && (
                                        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                                            {suggestions.map((s) => (
                                                <button key={s} type="button" onClick={() => { setSearch(s); setSuggestions([]); }}
                                                    className="block w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 dark:hover:bg-gray-700">{s}</button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button type="button" onClick={viewEmployee}
                                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
                                    <Search className="w-4 h-4" /> View
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {field('EmployeeName', 'Employee Name', { readOnly: true })}
                                {field('EmployeeId', 'Employee Id', { readOnly: true })}
                                {field('Department', 'Department', { readOnly: true })}
                                {field('Position', 'Position', { readOnly: true })}
                                {field('EmpStartDate', 'Employee Start Date', { readOnly: true })}
                            </div>
                        </Card>

                        <Card title="Vesting">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                {field('VestingStartDate', 'Vesting Start Date', { date: true })}
                                {field('VestingEndDate', 'Vesting End Date', { date: true })}
                                {field('VestingPercentage', 'Vesting Percentage', { num: true })}
                                {field('ExercisePricePerShare', 'Exercise Price Per Share', { num: true })}
                            </div>
                        </Card>

                        <Card title="Grant">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                {field('GrantID', 'Grant ID')}
                                {field('GrantDate', 'Grant Date', { date: true })}
                                {field('NumberofSharesGranted', 'Number of Shares Granted', { num: false })}
                                {field('GrantStatus', 'Grant Status', { select: STATUS_TYPES })}
                            </div>
                        </Card>

                        <Card title="Exercise">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {field('OptionID', 'Option ID')}
                                {field('ExerciseDate', 'Exercise Date', { date: true })}
                                {field('NumberofSharesExercised', 'Number of Shares Exercised', { num: false })}
                                {field('ExercisePrice', 'Exercise Price', { num: true })}
                                <div>
                                    <Label>Exercise Amount</Label>
                                    <input className={readonlyCls} readOnly value={exerciseAmount} />
                                </div>
                                {field('ExerciseStatus', 'Exercise Status', { select: STATUS_TYPES })}
                            </div>
                        </Card>

                        <Card>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <Label required>Compliance Upload Docs (PDF)</Label>
                                    <input key={fileKey} type="file" accept="application/pdf,.pdf" className={inputCls} onChange={pickPdf} disabled={busy} />
                                </div>
                                {field('Date', 'Date', { date: true })}
                                <div>
                                    <Label required>Remarks</Label>
                                    <textarea rows={1} className={inputCls} value={f.Remarks} onChange={(e) => set('Remarks', e.target.value)} disabled={busy} />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-5">
                                <button type="button" onClick={reset} disabled={busy}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button type="button" onClick={handleSubmit} disabled={busy}
                                    className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit
                                </button>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
};

export default ShareCreationDetails;
